// メンバー一覧の一括操作の部品。選んだ人に対して、プルダウンを開いて値を選び「適用」で実行する。

import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Team, UserRow } from "../lib/api";

/** 開閉するプルダウン。開くのは同時に 1 つだけ（open / onToggle は呼び出し側が持つ）。外側のクリック・Esc で閉じる。 */
export function Dropdown({ label, open, onToggle, children }: { label: string; open: boolean; onToggle: (open: boolean) => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onToggle(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onToggle(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, onToggle]);
  return (
    <div className="dropdown" ref={ref}>
      <button type="button" className={`dropdown-toggle${open ? " open" : ""}`} aria-expanded={open} onClick={() => onToggle(!open)}>
        {label}<span aria-hidden>{open ? "▴" : "▾"}</span>
      </button>
      {open && <div className="dropdown-panel">{children}</div>}
    </div>
  );
}

/** 1 つを選んで適用する（権限・状態など）。 */
export function ChoicePicker<T extends string>({ name, options, onApply }: {
  name: string; options: { value: T; label: string }[]; onApply: (v: T) => void;
}) {
  const [value, setValue] = useState<T | null>(null);
  return (
    <>
      <div className="dropdown-list">
        {options.map((o) => (
          <label key={o.value}>
            <input type="radio" name={name} checked={value === o.value} onChange={() => setValue(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
      <button type="button" className="primary dropdown-apply" disabled={value === null} onClick={() => value && onApply(value)}>適用</button>
    </>
  );
}

type TeamState = "on" | "off" | "mixed";

/**
 * 選んだ人の所属チームをまとめて変える。チェックは「全員が所属」、空は「誰も所属しない」、
 * 中間（−）は「一部が所属」でそのままにする。開いた時点の所属から始め、変えたチームだけを適用する。
 */
export function TeamPicker({ teams, users, onApply }: {
  teams: Team[]; users: UserRow[]; onApply: (change: Record<string, "on" | "off">) => void;
}) {
  const initial = (id: string): TeamState => {
    const n = users.filter((u) => u.teams.some((t) => t.teamId === id)).length;
    return n === 0 ? "off" : n === users.length ? "on" : "mixed";
  };
  const [state, setState] = useState<Record<string, TeamState>>(() => Object.fromEntries(teams.map((t) => [t.teamId, initial(t.teamId)])));
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = q ? teams.filter((t) => t.name.toLowerCase().includes(q)) : teams;
  // 一部が所属していたチームは、空 → 中間（そのまま）に戻せる。
  const next = (id: string): TeamState => {
    const cur = state[id];
    if (cur === "mixed" || cur === "off") return cur === "off" && initial(id) === "mixed" ? "mixed" : "on";
    return "off";
  };
  const change = Object.fromEntries(
    Object.entries(state).filter(([id, s]) => s !== "mixed" && s !== initial(id)),
  ) as Record<string, "on" | "off">;
  return (
    <>
      <div className="row">
        <input type="text" placeholder="チームを絞り込む" value={query} onChange={(e) => setQuery(e.target.value)} />
        <HelpTip label="チームの選び方">
          <p>チェックの状態は、選んだ人の所属を表します。クリックで切り替え、「適用」で反映します。</p>
          <dl>
            <dt><input type="checkbox" checked readOnly tabIndex={-1} /></dt><dd>全員を入れる（すでに所属している人はそのまま）</dd>
            <dt><input type="checkbox" checked={false} readOnly tabIndex={-1} /></dt><dd>全員から外す</dd>
            <dt><TriCheck state="mixed" onChange={() => {}} /></dt><dd>一部だけ所属。変えずにそのままにする（一部だけ所属していたチームだけに出ます）</dd>
          </dl>
        </HelpTip>
      </div>
      <div className="dropdown-list scroll">
        {shown.length === 0 && <p className="muted">該当するチームはありません。</p>}
        {shown.map((t) => (
          <label key={t.teamId}>
            <TriCheck state={state[t.teamId]} onChange={() => setState({ ...state, [t.teamId]: next(t.teamId) })} />
            {t.name}
          </label>
        ))}
      </div>
      <button type="button" className="primary dropdown-apply" disabled={Object.keys(change).length === 0} onClick={() => onApply(change)}>適用</button>
    </>
  );
}

function TriCheck({ state, onChange }: { state: TeamState; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = state === "mixed"; }, [state]);
  return <input ref={ref} type="checkbox" checked={state === "on"} onChange={onChange} />;
}

/** 「?」を押すと説明を出す。もう一度押すか、外側のクリック・Esc で閉じる。 */
export function HelpTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey, true); };
  }, [open]);
  return (
    <span className="help-tip" ref={ref}>
      <button type="button" className="help-tip-button" aria-label={label} aria-expanded={open} onClick={() => setOpen(!open)}>?</button>
      {open && <div className="help-tip-panel" role="tooltip"><strong>{label}</strong>{children}</div>}
    </span>
  );
}
